import React, { useState } from "react";

/**
 * Renders a brand logo. Priority chain:
 *   1. Local file  (client-supplied artwork)
 *   2. Clearbit    (public web logo API)
 *   3. Styled text (safe fallback so the grid never has gaps)
 *
 * Every logo is capped to the same maximum height + width so the wall
 * reads as a uniform, cohesive grid regardless of source cropping.
 * A `brightness(0) invert(1)` filter unifies every logo to cream on
 * the dark background.
 */
const BrandLogo = ({ name, logo, domain }) => {
  const [stage, setStage] = useState(logo ? "local" : domain ? "clearbit" : "text");

  const advance = () => {
    setStage((s) => (s === "local" && domain ? "clearbit" : "text"));
  };

  // These three source logos are squarer/lighter, so bump their visual size to match the other wordmarks.
  const isBoosted = /omnicom|talented|upakarma/i.test((logo || "") + " " + name);

  if (stage === "text") {
    return (
      <div className="h-10 md:h-12 w-full flex items-center justify-center">
        <span className="font-heading font-black text-xs md:text-sm text-ink/70 tracking-[-0.01em] text-center leading-tight px-2 hover:text-ink transition-colors">
          {name}
        </span>
      </div>
    );
  }

  const src = stage === "local" ? logo : `https://logo.clearbit.com/${domain}`;

  return (
    <div
      className={`w-full flex items-center justify-center px-4 ${
        isBoosted ? "h-14 md:h-16" : "h-10 md:h-12"
      }`}
      title={name}
    >
      <img
        src={src}
        alt={name}
        loading="lazy"
        onError={advance}
        className="h-full w-full object-contain opacity-85 hover:opacity-100 transition-opacity duration-500"
        style={{
          filter: "brightness(0) invert(1)",
          maxHeight: isBoosted ? "3.25rem" : "1.75rem",
          maxWidth: isBoosted ? "8.5rem" : "6.5rem",
        }}
      />
    </div>
  );
};

export default BrandLogo;
